import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Search, Trash2, Truck, X } from "lucide-react";
import { toast } from "react-toastify";
import { Trans, useTranslation } from "react-i18next";
import Button from "../../components/UI/Button";
import { Modal } from "../../components/UI/Modal";
import { Input } from "../../components/UI/FormField";
import { truckGroupAPI } from "../../services/api";
import { withVersion } from "../../utils/concurrency";
import type { GroupTruck, TruckGroup } from "../../types/truckGroups";
import { vehicleLabel, vehiclePlateHint } from "../../utils/vehicleLabel";

/**
 * Truck Groups — named, overlapping sets of trucks.
 *
 * Self-contained on purpose: its own API, its own local state, no Redux slice.
 * Nothing else in the app reads groups, so there is no shared state to keep in
 * sync, and the vehicles page and slice are left exactly as they were.
 *
 * Two ways to change membership, with different meanings:
 *   - "Add trucks" ADDS — the truck keeps every group it was already in.
 *   - Dragging a row onto another group MOVES — it leaves the current group.
 */

const DRAG_TYPE = "application/x-truck-id";

const matches = (truck: GroupTruck, term: string) =>
  !term ||
  truck.registrationNumber.toLowerCase().includes(term) ||
  (truck.displayName ?? "").toLowerCase().includes(term) ||
  (truck.markAndModel ?? "").toLowerCase().includes(term);

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || fallback;

type NameModal = { mode: "create" } | { mode: "rename"; group: TruckGroup };

export const TruckGroupsPage: React.FC = () => {
  const { t } = useTranslation("vehicles");

  const [groups, setGroups] = useState<TruckGroup[]>([]);
  const [fleet, setFleet] = useState<GroupTruck[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [memberSearch, setMemberSearch] = useState("");

  const [nameModal, setNameModal] = useState<NameModal | null>(null);
  const [nameDraft, setNameDraft] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [pickSearch, setPickSearch] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [groupsRes, trucksRes] = await Promise.all([
        truckGroupAPI.list(),
        truckGroupAPI.trucks(),
      ]);
      const loaded = groupsRes.data.data.groups;
      setGroups(loaded);
      setFleet(trucksRes.data.data.trucks);
      setActiveId((prev) =>
        prev && loaded.some((g) => g._id === prev)
          ? prev
          : (loaded[0]?._id ?? null),
      );
    } catch (error) {
      toast.error(errorMessage(error, t("truckGroups.toasts.loadError")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  /** Swap updated groups in from a server response. */
  const replaceGroups = (updated: TruckGroup[]) =>
    setGroups((prev) =>
      prev.map((g) => updated.find((u) => u._id === g._id) ?? g),
    );

  /**
   * Run a mutation, surfacing the server's message on failure. A failed write
   * reloads, since the local copy may be what was stale (a group deleted or
   * renamed in another tab).
   */
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (error) {
      toast.error(errorMessage(error, t("truckGroups.toasts.error")));
      load();
    } finally {
      setBusy(false);
    }
  };

  const activeGroup = groups.find((g) => g._id === activeId) ?? null;

  /** truckId → groups it belongs to, for the "Also in" tags. */
  const groupsByTruck = useMemo(() => {
    const map = new Map<string, TruckGroup[]>();
    for (const g of groups) {
      for (const id of g.vehicleIds) {
        map.set(id, [...(map.get(id) ?? []), g]);
      }
    }
    return map;
  }, [groups]);

  const otherGroups = (truckId: string, excludeId: string | null) =>
    (groupsByTruck.get(truckId) ?? []).filter((g) => g._id !== excludeId);

  const memberTerm = memberSearch.trim().toLowerCase();
  const visibleMembers = activeGroup
    ? activeGroup.vehicles.filter((v) => matches(v, memberTerm))
    : [];

  const pickTerm = pickSearch.trim().toLowerCase();
  const candidates = activeGroup
    ? fleet.filter(
        (v) => !activeGroup.vehicleIds.includes(v._id) && matches(v, pickTerm),
      )
    : [];

  // ── Group create / rename / delete ────────────────────────────────────────

  const selectGroup = (id: string | null) => {
    setActiveId(id);
    setMemberSearch("");
  };

  const openCreate = () => {
    setNameDraft("");
    setNameModal({ mode: "create" });
  };

  const openRename = (group: TruckGroup) => {
    setNameDraft(group.name);
    setNameModal({ mode: "rename", group });
  };

  const submitName = (e: React.FormEvent) => {
    e.preventDefault();
    const name = nameDraft.trim();
    if (!name || !nameModal) return;

    run(async () => {
      if (nameModal.mode === "create") {
        const res = await truckGroupAPI.create({ name });
        const group = res.data.data.group;
        setGroups((prev) => [...prev, group]);
        selectGroup(group._id);
        toast.success(t("truckGroups.toasts.created", { name: group.name }));
      } else {
        const res = await truckGroupAPI.rename(
          nameModal.group._id,
          withVersion({ name }, nameModal.group),
        );
        replaceGroups([res.data.data.group]);
        toast.success(t("truckGroups.toasts.renamed"));
      }
      setNameModal(null);
    });
  };

  const deleteGroup = (group: TruckGroup) => {
    if (!window.confirm(t("truckGroups.confirmDelete", { name: group.name })))
      return;

    run(async () => {
      await truckGroupAPI.delete(group._id);
      const remaining = groups.filter((g) => g._id !== group._id);
      setGroups(remaining);
      if (activeId === group._id) selectGroup(remaining[0]?._id ?? null);
      toast.success(t("truckGroups.toasts.deleted"));
    });
  };

  // ── Membership ────────────────────────────────────────────────────────────

  const removeTruck = (group: TruckGroup, truck: GroupTruck) =>
    run(async () => {
      const res = await truckGroupAPI.removeTruck(group._id, truck._id);
      replaceGroups([res.data.data.group]);
      toast.success(
        t("truckGroups.toasts.removed", {
          plate: vehicleLabel(truck),
          name: group.name,
        }),
      );
    });

  const openAdd = () => {
    setPicked(new Set());
    setPickSearch("");
    setAddOpen(true);
  };

  const togglePick = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const confirmAdd = () => {
    if (!activeGroup || picked.size === 0) return;
    const count = picked.size;
    run(async () => {
      const res = await truckGroupAPI.addTrucks(activeGroup._id, [...picked]);
      replaceGroups([res.data.data.group]);
      toast.success(
        t("truckGroups.toasts.added", { count, name: activeGroup.name }),
      );
      setAddOpen(false);
    });
  };

  const handleDrop = (target: TruckGroup, e: React.DragEvent) => {
    e.preventDefault();
    setDragOverId(null);
    const truckId = e.dataTransfer.getData(DRAG_TYPE);
    const source = activeGroup;
    if (!truckId || !source || target._id === source._id) return;

    const truck = source.vehicles.find((v) => v._id === truckId);
    if (!truck) return;

    if (target.vehicleIds.includes(truckId)) {
      toast.info(
        t("truckGroups.toasts.alreadyIn", {
          plate: vehicleLabel(truck),
          name: target.name,
        }),
      );
      return;
    }

    run(async () => {
      const res = await truckGroupAPI.moveTruck({
        vehicleId: truckId,
        fromGroupId: source._id,
        toGroupId: target._id,
      });
      replaceGroups(res.data.data.groups);
      toast.success(
        t("truckGroups.toasts.moved", {
          plate: vehicleLabel(truck),
          from: source.name,
          to: target.name,
        }),
      );
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const tag = (g: TruckGroup) => (
    <span
      key={g._id}
      className="rounded-full bg-[#F0EFEA] px-2 py-0.5 text-[10px] font-bold text-[#8A9590]"
    >
      {g.name}
    </span>
  );

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#C97A3C]" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start justify-between gap-4 max-sm:flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-dark-900">
            {t("truckGroups.title")}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {t("truckGroups.subtitle")}
          </p>
        </div>
        <Button onClick={openCreate} variant="primary" disabled={busy}>
          <Plus className="h-4 w-4" />
          <span>{t("truckGroups.newGroup")}</span>
        </Button>
      </div>

      {groups.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white px-6 py-16 text-center">
          <Truck className="mx-auto mb-3 h-10 w-10 text-gray-300" />
          <p className="font-medium text-gray-900">
            {t("truckGroups.empty.noGroups")}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {t("truckGroups.empty.noGroupsHint")}
          </p>
          <Button onClick={openCreate} variant="primary" className="mt-5">
            <Plus className="h-4 w-4" />
            <span>{t("truckGroups.newGroup")}</span>
          </Button>
        </div>
      ) : (
        <div className="flex gap-6 max-md:flex-col">
          {/* Groups list — also the drop targets for "move". */}
          <aside className="w-full shrink-0 md:w-64">
            <ul className="rounded-lg border border-gray-200 bg-white p-2">
              {groups.map((g) => {
                const active = g._id === activeId;
                const dropTarget = dragOverId === g._id;
                return (
                  <li key={g._id}>
                    <button
                      type="button"
                      onClick={() => selectGroup(g._id)}
                      onDragOver={(e) => {
                        if (!draggingId || active) return;
                        e.preventDefault();
                        setDragOverId(g._id);
                      }}
                      onDragLeave={() =>
                        setDragOverId((id) => (id === g._id ? null : id))
                      }
                      onDrop={(e) => handleDrop(g, e)}
                      className={`mb-0.5 flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left text-sm transition-colors ${
                        active
                          ? "bg-[#0B0F0E] text-white"
                          : dropTarget
                            ? "bg-[#FBEEDD] outline-2 outline-dashed outline-[#C97A3C]"
                            : "hover:bg-[#FBEEDD]"
                      }`}
                    >
                      <span className="truncate font-semibold">{g.name}</span>
                      <span
                        className={`ml-2 text-xs ${active ? "text-gray-300" : "text-gray-400"}`}
                      >
                        {g.vehicleIds.length}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* Selected group */}
          {activeGroup && (
            <section className="min-w-0 flex-1">
              <div className="mb-4 flex items-center gap-3 max-sm:flex-wrap">
                <div className="min-w-0">
                  <h2 className="truncate text-lg font-bold text-gray-900">
                    {activeGroup.name}
                  </h2>
                  <p className="text-xs text-gray-500">
                    {t("truckGroups.truckCount", {
                      count: activeGroup.vehicleIds.length,
                    })}
                  </p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openRename(activeGroup)}
                    disabled={busy}
                  >
                    <Pencil className="h-4 w-4" />
                    <span>{t("truckGroups.rename")}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteGroup(activeGroup)}
                    disabled={busy}
                    className="text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>{t("truckGroups.deleteGroup")}</span>
                  </Button>
                </div>
              </div>

              <div className="mb-4 flex items-center justify-between gap-4 max-sm:flex-wrap">
                <div className="relative w-full max-w-[280px]">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    type="text"
                    placeholder={t("truckGroups.searchMembers")}
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    className="bg-white pl-10"
                  />
                </div>
                <Button onClick={openAdd} variant="primary" disabled={busy}>
                  <Plus className="h-4 w-4" />
                  <span>{t("truckGroups.addTrucks")}</span>
                </Button>
              </div>

              {groups.length > 1 && activeGroup.vehicles.length > 0 && (
                <p className="mb-4 rounded-lg border-2 border-dashed border-gray-200 px-4 py-3 text-center text-xs text-gray-500">
                  <Trans
                    t={t}
                    i18nKey="truckGroups.dragHint"
                    components={{ b: <b /> }}
                  />
                </p>
              )}

              {activeGroup.vehicles.length === 0 ? (
                <div className="rounded-lg border border-gray-200 bg-white px-6 py-12 text-center text-sm text-gray-500">
                  {t("truckGroups.empty.noMembers")}
                </div>
              ) : visibleMembers.length === 0 ? (
                <div className="rounded-lg border border-gray-200 bg-white px-6 py-12 text-center text-sm text-gray-500">
                  {t("truckGroups.empty.noMatch")}
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F5F4F0] text-left text-[11px] font-bold uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-4 py-3">
                          {t("truckGroups.columns.plate")}
                        </th>
                        <th className="px-4 py-3">
                          {t("truckGroups.columns.model")}
                        </th>
                        <th className="px-4 py-3">
                          {t("truckGroups.columns.alsoIn")}
                        </th>
                        <th className="w-12 px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {visibleMembers.map((truck) => {
                        const others = otherGroups(truck._id, activeGroup._id);
                        return (
                          <tr
                            key={truck._id}
                            draggable={!busy}
                            onDragStart={(e) => {
                              e.dataTransfer.setData(DRAG_TYPE, truck._id);
                              e.dataTransfer.effectAllowed = "move";
                              setDraggingId(truck._id);
                            }}
                            onDragEnd={() => {
                              setDraggingId(null);
                              setDragOverId(null);
                            }}
                            className={`cursor-grab border-t border-gray-100 ${
                              draggingId === truck._id ? "opacity-40" : ""
                            }`}
                          >
                            <td className="px-4 py-3">
                              <div className="font-mono font-bold">
                                {vehicleLabel(truck)}
                              </div>
                              {vehiclePlateHint(truck) && (
                                <div className="font-mono text-xs text-gray-500">
                                  {vehiclePlateHint(truck)}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {truck.markAndModel || "—"}
                            </td>
                            <td className="px-4 py-3">
                              {others.length ? (
                                <div className="flex flex-wrap gap-1">
                                  {others.map(tag)}
                                </div>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                title={t("truckGroups.removeFromGroup")}
                                aria-label={t("truckGroups.removeFromGroup")}
                                disabled={busy}
                                onClick={() => removeTruck(activeGroup, truck)}
                                className="rounded-md border border-gray-200 p-1 text-gray-400 hover:border-[#C97A3C] hover:bg-[#FBEEDD] hover:text-[#C97A3C] disabled:opacity-50"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {/* Create / rename */}
      <Modal
        isOpen={!!nameModal}
        onClose={() => setNameModal(null)}
        title={
          nameModal?.mode === "rename"
            ? t("truckGroups.modal.renameTitle")
            : t("truckGroups.modal.createTitle")
        }
        size="sm"
      >
        <form onSubmit={submitName} className="p-6">
          <label className="mb-1.5 block text-sm font-medium text-gray-700">
            {t("truckGroups.modal.nameLabel")}
          </label>
          <Input
            autoFocus
            maxLength={100}
            value={nameDraft}
            placeholder={t("truckGroups.modal.namePlaceholder")}
            onChange={(e) => setNameDraft(e.target.value)}
          />
          <div className="mt-6 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setNameModal(null)}
            >
              {t("truckGroups.modal.cancel")}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={busy || !nameDraft.trim()}
            >
              {nameModal?.mode === "rename"
                ? t("truckGroups.modal.save")
                : t("truckGroups.modal.create")}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Add trucks — adds without removing from other groups */}
      <Modal
        isOpen={addOpen && !!activeGroup}
        onClose={() => setAddOpen(false)}
        title={t("truckGroups.modal.addTitle", {
          name: activeGroup?.name ?? "",
        })}
        size="md"
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="border-b border-gray-100 p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                autoFocus
                placeholder={t("truckGroups.searchFleet")}
                value={pickSearch}
                onChange={(e) => setPickSearch(e.target.value)}
                className="bg-white pl-10"
              />
            </div>
          </div>

          <div className="max-h-[50vh] overflow-y-auto p-2">
            {fleet.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-gray-500">
                {t("truckGroups.empty.noFleet")}
              </p>
            ) : candidates.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-gray-500">
                {t("truckGroups.empty.noCandidates")}
              </p>
            ) : (
              candidates.map((truck) => (
                <label
                  key={truck._id}
                  className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 hover:bg-[#F5F4F0]"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[#C97A3C]"
                    checked={picked.has(truck._id)}
                    onChange={() => togglePick(truck._id)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm font-bold">
                      {vehicleLabel(truck)}
                    </div>
                    {(vehiclePlateHint(truck) || truck.markAndModel) && (
                      <div className="text-xs text-gray-500">
                        {[vehiclePlateHint(truck), truck.markAndModel]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap justify-end gap-1">
                    {otherGroups(truck._id, activeGroup?._id ?? null).map(tag)}
                  </div>
                </label>
              ))
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-100 p-4">
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              {t("truckGroups.modal.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={confirmAdd}
              disabled={busy || picked.size === 0}
            >
              {t("truckGroups.modal.addConfirm", { count: picked.size })}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
